"use client";

import { LoginSchema, LoginType } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { loginWorker } from "@/services/auth";
import { getSession } from "next-auth/react";
import { CUSTOMER_USER_ROLE, FEDERAL_WORKER_USERTYPE } from "@/lib/constants";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";

export default function WorkerLogin() {
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const methods = useForm<LoginType>({ resolver: zodResolver(LoginSchema) });
  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = methods;

  const password = watch("password");
  const ippis = watch("ippis");

  const isFormFilled = Boolean(password && ippis);
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");

  const onSubmit = async (data: LoginType) => {
    setLoginLoading(true);
    try {
      const res = await loginWorker({
        identifier: ippis.trim().toLowerCase(),
        password: data.password,
        userType: CUSTOMER_USER_ROLE,
      });
      if (res?.error) {
        setLoginLoading(false);
        toast.error(res.error);
        return;
      }

      const session = await getSession();

      if (!session) {
        throw new Error("Error signing in. Please try again.");
      }

      const redirectTo = callbackUrl && callbackUrl !== "/" ? callbackUrl : "/shop";
      router.push(redirectTo);
    } catch (error) {
      setLoginLoading(false);
      // todo: placeholder error message, replace with actual error message
      toast.error(error instanceof Error ? error.message : "An error occured");
      return;
    }
  };
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const featureFlagGoogleSignIn = process.env.NEXT_PUBLIC_FEATURE_FLAG_GOOGLE_SIGN_IN === "true";
  return (
    <div className=''>
      <div
        className={cn(
          "text-link-primary mx-auto mb-2 flex w-fit items-center gap-1 text-4xl font-medium md:text-5xl lg:text-6xl 2xl:mt-[5%] 2xl:mb-5"
        )}
      >
        <Image
          src={mounted && theme === "dark" ? "/assets/icons/logo-dark.svg" : "/assets/icons/logo-light.svg"}
          alt='Onyx Logo'
          width={82}
          height={82}
          className='mr-2 inline-flex size-12 md:size-14 lg:size-16'
        />
        Onyx <span className='text-link-primary'>.</span>
      </div>
      <h1 className='text-text-title text-center text-2xl leading-[120%] font-semibold md:text-4xl lg:px-6'>
        Welcome to Onyx<span className='text-link-primary'>.</span>
        <br />
        <span className='text-text-title/70 text-xl md:text-3xl'>Credit that shines.</span>
      </h1>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className='mx-auto mt-5 flex w-full flex-col gap-4.5'>
          <div className='flex flex-col gap-2.5'>
            <Label className='px-2.5'>IPPIS Number</Label>
            <div className='relative w-full'>
              <Input
                aria-invalid={errors.ippis ? "true" : "false"}
                // type='number'
                className='px-6'
                placeholder='Input your IPPIS number'
                {...register("ippis")}
              />
            </div>
            {errors.ippis && (
              <p aria-invalid='true' className='text-text-destructive text-xs'>
                {errors.ippis.message}
              </p>
            )}
          </div>
          <div className='flex flex-col gap-2.5'>
            <Label className='px-2.5'>Password</Label>
            <div className='relative w-full'>
              <Input
                aria-invalid={errors.password ? "true" : "false"}
                type={showPassword ? "text" : "password"}
                className='pr-10 pl-6'
                placeholder='Password'
                {...register("password")}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='text-text-label absolute top-1/2 right-3 -translate-y-1/2 transform'
              >
                {showPassword ? (
                  <EyeSlashIcon className='text-icon-title size-4 cursor-pointer stroke-[1.8px]' />
                ) : (
                  <EyeIcon className='text-icon-title size-4 cursor-pointer stroke-[1.8px]' />
                )}
              </button>
            </div>
            {errors.password && (
              <p aria-invalid='true' className='text-text-destructive text-xs'>
                {errors.password.message}
              </p>
            )}
            <Link
              href={`/forgot-password/${FEDERAL_WORKER_USERTYPE}`}
              className='text-link-primary block text-right text-base font-semibold'
            >
              Forgot password?
            </Link>
          </div>

          <Button
            type='submit'
            loading={loginLoading}
            disabled={!isFormFilled || loginLoading}
            className='mx-auto mt-5 w-full py-6 text-xs md:w-3/4 lg:mt-0'
          >
            Log in
          </Button>
        </form>
      </FormProvider>
      {featureFlagGoogleSignIn && (
        <>
          <p className='text-text-label my-4 text-center text-xs'>or</p>
          <Button variant='outline' className='mx-auto mt-5 flex w-full py-6 text-xs md:w-3/4 lg:mt-0'>
            <Image src='/assets/icons/google-symbol.svg' alt='Google' width={20} height={20} />
            <span>Continue with Google</span>
          </Button>
        </>
      )}
      <p className='text-text-label my-4 text-center text-xs'>
        Don&apos;t have an account?{" "}
        <Link className='text-link-primary font-semibold' href={`/register/${FEDERAL_WORKER_USERTYPE}/sign-up`}>
          Register
        </Link>
      </p>
    </div>
  );
}
